import { usePriorityOptions } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { LocationMap } from "@shared/components/LocationMap";
import { PageHeader } from "@shared/components/PageHeader";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { ArrowLeft, Eye, FileText, MapPin } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddFailures,
  useAssetByMap,
  useFailureMode,
  useFailuresDetails,
  useObservedSymptom,
  useUpdateFailure,
} from "../../hooks";
import { AssetSearchTable } from "./assetTable";

const Create = () => {
  const navigate = useNavigate();
  const remoteUtilityId = getRemoteUtilityId().toString();
  const { id, action } = useParams();
  const { toast } = useToast();

  const addFailureMutation = useAddFailures();
  const updateFailureMutation = useUpdateFailure();
  const { data: priorityData } = usePriorityOptions();
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState("");
  const [linkedAsset, setLinkedAsset] = useState(null);
  const [selectedMapLocation, setSelectedMapLocation] = useState({
    lat: 0,
    lng: 0,
  });

  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [hasUserLocation, setHasUserLocation] = useState(false);
  const [assetDataForEdit, setAssetDataForEdit] = useState(null);
  const [existingDocuments, setExistingDocuments] = useState([]);

  // Add the failure details hook for edit mode
  const { data: failureDetails } = useFailuresDetails({
    remote_utility_id: remoteUtilityId,
    id: id,
  });

  // Use the useAssetByMap hook
  const { data: assetByMapData } = useAssetByMap({
    remote_utility_id: remoteUtilityId,
    latitude: selectedMapLocation.lat,
    longitude: selectedMapLocation.lng,
  });

  const { data: failureModeData } = useFailureMode({
    remote_utility_id: remoteUtilityId,
    asset_type_characteristics: "failure_mode",
    config_level: "asset_type_characteristics",
    code_list: selectedAsset?.assetTypeCode || selectedAssetType,
  });

  const { data: observedSymptomsData } = useObservedSymptom();

  // Helper function to map priority string to number
  const mapPriorityToAPI = (priorityKey, priorityData) => {
    if (!priorityData || !Array.isArray(priorityData)) return "";

    const priorityItem = priorityData.find(
      (item) => item.key.toString() === priorityKey.toString()
    );
    return priorityItem ? priorityKey.toString() : "";
  };

  const formatDateForAPI = (dateString: string) => {
    if (!dateString) return new Date().toISOString();

    try {
      const date = new Date(dateString);
      return date.toISOString();
    } catch (error) {
      console.error("Invalid date format:", dateString);
      return new Date().toISOString();
    }
  };
  // Helper function to map failure identification method number to string
  const mapFailureIdentificationMethod = (method: number) => {
    const methodMap: { [key: number]: string } = {
      0: "location",
      1: "map",
      2: "manual",
    };
    return methodMap[method] || "manual";
  };

  // Helper function to parse observed symptoms
  const parseObservedSymptoms = (symptoms: any[]) => {
    if (!symptoms || !Array.isArray(symptoms)) return [];
    try {
      const parsed = symptoms
        .map((symptom) => {
          if (typeof symptom === "string") {
            return JSON.parse(symptom);
          }
          return symptom;
        })
        .flat();
      return parsed;
    } catch (error) {
      return [];
    }
  };

  // Transform options
  const priorityOptions =
    priorityData?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const observedSymptomsOptions =
    observedSymptomsData?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const failureModeOptions =
    failureModeData?.result?.map((item: any) => ({
      value: item.code,
      label: item.name,
    })) || [];

  // Transform API response data for dropdown options
  const transformedAssets = useMemo(() => {
    if (!assetByMapData?.results) return [];
    return assetByMapData.results.map((asset: any) => ({
      id: asset.id.toString(),
      asset_no: asset.assetNo,
      name: asset.assetName,
      systemNetwork: asset.network || asset.system || "N/A",
      facility: asset.facilityName,
      assetTypeCode: asset.assetTypeCode,
      location: `Lat: ${asset.latitude?.toFixed(
        4
      )}, Lng: ${asset.longitude?.toFixed(4)}`,
      coordinates: { lat: asset.latitude, lng: asset.longitude },
      conditionScore: asset.conditionScores || 0,
      riskScore: asset.riskScores || 0,
      status: asset.statusDisplay,
      assetType: asset.assetTypeDisplay,
      manufacturer: asset.manufacturer,
    }));
  }, [assetByMapData]);

  // Helper functions for asset options in edit mode
  const getLocationAssetOptions = () => {
    let options = transformedAssets.map((asset) => ({
      value: asset.id,
      label: `${asset.name} (${asset.location})`,
      description: `${asset.facility} | Score: ${asset.conditionScore}`,
    }));

    // If in edit mode and we have failure details but asset not in current options
    if (
      action === "edit" &&
      failureDetails &&
      failureDetails.failureIdentificationMethod === 0
    ) {
      const assetId = failureDetails.asset.id?.toString();
      const hasAsset = options.find((opt) => opt.value === assetId);

      if (!hasAsset && assetId) {
        // Add the current asset as an option at the beginning
        options.unshift({
          value: assetId,
          label: `${failureDetails.asset?.assetNo || assetId} - ${
            failureDetails.asset?.assetName ||
            failureDetails.asset?.name ||
            `Asset ${assetId}`
          } (Current Selection)`,
          description: `${
            failureDetails.asset?.facilityName ||
            failureDetails.asset?.facility ||
            "Unknown Facility"
          } | Previously selected asset`,
        });
      }
    }

    return options;
  };

  const getMapAssetOptions = () => {
    let options = transformedAssets.map((asset) => ({
      value: asset.id,
      label: `${asset.asset_no || asset.id} - ${asset.name}`,
      description: `${asset.assetTypeCode}`,
    }));

    // If in edit mode and we have failure details but asset not in current options
    if (
      action === "edit" &&
      failureDetails &&
      failureDetails.failureIdentificationMethod === 1
    ) {
      const assetId = failureDetails.asset.id?.toString();
      const hasAsset = options.find((opt) => opt.value === assetId);

      if (!hasAsset && assetId) {
        // Add the current asset as an option at the beginning
        options.unshift({
          value: assetId,
          label: `${failureDetails.asset?.assetNo || assetId} - ${
            failureDetails.asset?.assetName
          } (Current Selection)`,
          description: `${failureDetails.asset?.assetTypeCode}`,
        });
      }
    }

    return options;
  };

  // Function to get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      const error = "Geolocation is not supported by this browser.";
      setLocationError(error);
      toast({
        title: "Location Error",
        description: error,
        variant: "destructive",
      });
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedMapLocation({ lat: latitude, lng: longitude });
        setHasUserLocation(true);
        setLocationLoading(false);

        toast({
          title: "Location Found",
          description: `Current location: ${latitude.toFixed(
            4
          )}, ${longitude.toFixed(4)}`,
        });
      },
      (error) => {
        setLocationLoading(false);
        let errorMessage = "";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location access denied by user. Please enable location permissions.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
          default:
            errorMessage =
              "An unknown error occurred while retrieving location.";
            break;
        }

        setLocationError(errorMessage);
        toast({
          title: "Location Error",
          description: errorMessage,
          variant: "destructive",
        });
      },
      options
    );
  };

  const getFailureReportFields = (): FormField[] => [
    {
      name: "assetIdentificationMethod",
      type: "select",
      label: "How would you like to identify the asset?",
      group: "Asset Identification",
      required: true,
      groupColorNeeded: false,
      placeholder: "Select identification method",
      options: [
        { value: "location", label: "Search Assets in My Location" },
        { value: "map", label: "Search Assets on Map" },
        { value: "manual", label: "Manual Search by ID/Name" },
      ],
      className: "col-span-2",
      helperText: "Choose how you want to find and select the failed asset",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Search Assets in My Location:</strong> Find assets near your
            current GPS location
          </li>
          <li>
            <strong>Search Assets on Map:</strong> Use interactive map to locate
            and select assets
          </li>
          <li>
            <strong>Manual Search by ID/Name:</strong> Search using asset ID,
            name, or other identifiers
          </li>
        </ul>
      ),
      showHelperTooltip: true,
    },
    {
      name: "locationAssetSelection",
      type: "select",
      label: "Select Detected Asset",
      group: "Asset Identification",
      required: false,
      groupColorNeeded: false,
      placeholder: locationLoading
        ? "Getting your location..."
        : locationError
        ? "Location unavailable - click to retry"
        : transformedAssets.length === 0
        ? "No assets found in your area"
        : "Select detected asset",
      showWhen: (formValues) =>
        formValues.assetIdentificationMethod === "location",
      options: getLocationAssetOptions(),
      className: "col-span-2",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Automatically detects assets within range of your current location
          </li>
          <li>Uses GPS coordinates to find nearby infrastructure</li>
          <li>
            Click "Get My Location" to enable location-based asset detection
          </li>
          <li>
            If no assets appear, try expanding search radius or use map
            selection
          </li>
        </ul>
      ),
      showHelperTooltip: true,
      customRender: (field, form, error) => {
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">
                  {locationLoading
                    ? "Getting your location..."
                    : hasUserLocation
                    ? `Location: ${selectedMapLocation.lat.toFixed(
                        4
                      )}, ${selectedMapLocation.lng.toFixed(4)}`
                    : "Location not detected"}
                </span>
                {locationLoading && (
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                )}
              </div>

              <div className="flex gap-2">
                {locationError && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={getCurrentLocation}
                    className="text-xs"
                  >
                    Retry Location
                  </Button>
                )}
                {!locationLoading && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={getCurrentLocation}
                    className="text-xs"
                  >
                    {hasUserLocation ? "Update Location" : "Get My Location"}
                  </Button>
                )}
              </div>
            </div>
            {locationError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{locationError}</p>
                <p className="text-xs text-red-500 mt-1">
                  You can manually select a location on the map below or try
                  getting your location again.
                </p>
              </div>
            )}
            <LocationMap
              latitude={selectedMapLocation.lat}
              longitude={selectedMapLocation.lng}
              onLocationChange={handleMapLocationChange}
              onAddressChange={handleMapAddressChange}
              className="h-full"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                {...form.register(field.name, { required: field.required })}
                disabled={locationLoading}
              >
                <option value="">{field.placeholder}</option>
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {field.helperText && (
                <p className="text-sm text-gray-500 mt-1">{field.helperText}</p>
              )}
              {transformedAssets.length === 0 && hasUserLocation && (
                <p className="text-sm text-orange-600 mt-1">
                  No assets found within range of your location. Try expanding
                  the search or use map selection.
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      name: "mapAssetSelection",
      type: "select",
      label: "Selected Asset from Map",
      group: "Asset Identification",
      required: false,
      groupColorNeeded: false,
      placeholder: "No asset selected from map",
      showWhen: (formValues) => formValues.assetIdentificationMethod === "map",
      options: getMapAssetOptions(),
      className: "col-span-2",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Use the interactive map to browse and select assets visually</li>
          <li>Click on asset markers to view details and select</li>
          <li>Pan and zoom to explore different areas</li>
          <li>
            Best for when you know the approximate location of the failed asset
          </li>
        </ul>
      ),
      showHelperTooltip: true,
      customRender: (field, form, error) => {
        return (
          <div className="space-y-3">
            <LocationMap
              latitude={selectedMapLocation.lat}
              longitude={selectedMapLocation.lng}
              onLocationChange={handleMapLocationChange}
              onAddressChange={handleMapAddressChange}
              className="h-full"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                {...form.register(field.name, { required: field.required })}
              >
                <option value="">{field.placeholder}</option>
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {field.helperText && (
                <p className="text-sm text-gray-500 mt-1">{field.helperText}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      name: "assetSearchTable",
      type: "text",
      label: "Asset Search & Selection",
      group: "Asset Identification",
      required: false,
      groupColorNeeded: false,
      showWhen: (formValues) =>
        formValues.assetIdentificationMethod === "manual",
      className: "col-span-2",
      helperText: "Search and select assets using the table below",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Search assets by ID, name, facility, or other identifiers</li>
          <li>Use filters to narrow down results by facility or network</li>
          <li>Best option when you know specific asset details</li>
          <li>Supports advanced search with multiple criteria</li>
        </ul>
      ),
      showHelperTooltip: true,
      customRender: (field, form, error) => {
        return (
          <div className="w-full">
            <AssetSearchTable
              onAssetSelect={onAssetSelect}
              linkedAsset={linkedAsset}
              setLinkedAsset={setLinkedAsset}
            />
          </div>
        );
      },
    },
    {
      name: "priority",
      type: "select",
      label: "Priority Level",
      group: "Failure Details",
      required: true,
      groupColorNeeded: false,
      placeholder: "Select priority level",
      options: priorityOptions,
      className: "col-span-1",
      helperText: "Select the urgency level based on service impact",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Critical:</strong> Immediate safety risk or complete service
            stoppage
          </li>
          <li>
            <strong>High:</strong> Significant operational impact, resolve same
            day
          </li>
          <li>
            <strong>Medium:</strong> Moderate impact, resolve within 48 hours
          </li>
          <li>
            <strong>Low:</strong> Minor impact, can be scheduled for routine
            maintenance
          </li>
        </ul>
      ),
      showHelperTooltip: true,
    },
    {
      name: "failureMode",
      type: "select",
      label: "Failure Mode",
      group: "Failure Details",
      groupColorNeeded: false,
      required: false,
      placeholder: "Select failure mode if known",
      options: failureModeOptions,
      className: "col-span-1",
      helperText: "Specific type of failure if identifiable",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Category of failure type for analysis and reporting</li>
          <li>
            Common modes: Mechanical, Electrical, Hydraulic, Structural,
            Operational
          </li>
          <li>Helps maintenance teams prepare appropriate tools and parts</li>
          <li>Used for trend analysis and predictive maintenance planning</li>
        </ul>
      ),
      showHelperTooltip: true,
    },
    {
      name: "description",
      type: "textarea",
      label: "Failure Description",
      group: "Failure Details",
      groupColorNeeded: false,
      required: true,
      placeholder:
        "Describe what happened, what you observed, and any immediate impacts...",
      rows: 4,
      className: "col-span-2",
      helperText: "Provide detailed description of the failure and its impacts",
      minLength: 10,
      maxLength: 1000,
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Detailed description of what happened and what you observed</li>
          <li>
            Include symptoms, sounds, visual indicators, and immediate impacts
          </li>
          <li>
            Be specific - this helps technicians diagnose and resolve faster
          </li>
          <li>Mention any environmental conditions or unusual circumstances</li>
        </ul>
      ),
      showHelperTooltip: true,
    },
    {
      name: "symptoms",
      type: "multi-select",
      label: "Observed Symptoms",
      group: "Failure Details",
      groupColorNeeded: false,
      required: false,
      placeholder: "Select all observed symptoms",
      options: observedSymptomsOptions,
      className: "col-span-1",
      maxSelection: 5,
      helperText: "Select all symptoms you observed (max 5)",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Select from common failure indicators</li>
          <li>Multiple symptoms can be selected (maximum 5)</li>
          <li>
            Helps categorize failures for trend analysis and predictive
            maintenance
          </li>
          <li>
            Examples: Strange noises, leaks, overheating, vibrations, etc.
          </li>
        </ul>
      ),
      showHelperTooltip: true,
    },
    {
      name: "failureDateTime",
      type: "datepicker",
      label: "Date of Failure",
      group: "Failure Details",
      groupColorNeeded: false,
      required: false,
      allowFuture: false,
      className: "col-span-1",
      helperText: "When did the failure occur?",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            When the actual failure occurred (may be different from report time)
          </li>
          <li>If unknown, estimate as closely as possible</li>
          <li>Used for failure analysis and maintenance scheduling</li>
          <li>Helps identify patterns and correlate with operational data</li>
        </ul>
      ),
      showHelperTooltip: true,
    },
    {
      name: "photos",
      type: "multi-file",
      label: "Photos/Videos",
      group: "Supporting Information",
      groupColorNeeded: false,
      required: false,
      accept: "image/*,video/*",
      multiple: true,
      helperText:
        "Upload visual evidence of the failure (max 10 files, 5MB each)",
      className: "col-span-2",
      maxSize: 5242880,
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Upload visual evidence of the failure condition</li>
          <li>Supported formats: Images (JPG, PNG) and Videos (MP4, MOV)</li>
          <li>Maximum file size: 5MB per file, up to 10 files</li>
          <li>Clear photos help technicians prepare and speed resolution</li>
        </ul>
      ),
      showHelperTooltip: true,
    },
    {
      name: "existingDocuments",
      type: "switch",
      label: "Existing Documents",
      group: "Supporting Information",
      groupColorNeeded: false,
      required: false,
      className: "col-span-2",
      helperText: "Documents previously uploaded for this failure report",
      showWhen: (formValues) =>
        action === "edit" && existingDocuments.length > 0,
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Documents that were previously uploaded with this failure report
          </li>
          <li>Click the eye icon to view documents in a new tab</li>
          <li>These documents will remain associated with this report</li>
          <li>Upload new documents using the Photos/Videos field above</li>
        </ul>
      ),
      showHelperTooltip: true,
      customRender: (field, form, error) => {
        return (
          <div className="space-y-3">
            <div>
              <div className="p-4  rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {existingDocuments.map((document) => (
                    <div
                      key={document.id}
                      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {document.fileName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {document.fileTypeDisplay}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <a
                          href={document.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded transition-colors"
                          title="View Document"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      name: "additionalNotes",
      type: "textarea",
      label: "Additional Notes",
      group: "Supporting Information",
      groupColorNeeded: false,
      required: false,
      placeholder:
        "Any other relevant information, temporary fixes applied, safety concerns, etc...",
      rows: 3,
      className: "col-span-2",
      maxLength: 500,
      helperText:
        "Include any temporary fixes, safety concerns, or other relevant details",
      tooltipText: (
        <ul className="list-disc pl-5 space-y-1">
          <li>Any other relevant information not covered above</li>
          <li>
            Include environmental conditions, recent maintenance, or related
            issues
          </li>
          <li>
            Mention any temporary fixes or safety measures already implemented
          </li>
          <li>
            Optional field but can provide valuable context for resolution
          </li>
        </ul>
      ),
      showHelperTooltip: true,
    },
  ];

  const [formFields, setFormFields] = useState(getFailureReportFields());

  const defaultFormData = {
    failureId: action === "edit" ? id : "",
    assetIdentificationMethod: "",
    locationAssetSelection: "",
    mapAssetSelection: "",
    selectedAssets: [],
    selectedAssetsInfo: "",
    priority: "",
    description: "",
    failureMode: "",
    symptoms: [],
    failureDateTime: "",
    additionalNotes: "",
    reportDateTime: new Date().toLocaleString(),
    reportedBy: "Current User",
    assetSearchTerm: "",
    facilityFilter: "",
    networkFilter: "",
    photos: [],
    impactAssessment: "",
    immediateActionRequired: false,
    emergencyContacts: "",
    temporaryMeasures: "",
  };

  const form = FormService.createForm({
    fields: formFields,
    defaultValues: defaultFormData,
  });

  const formValues = form.watch();

  // Update form fields when transformedAssets changes
  useEffect(() => {
    setFormFields(getFailureReportFields());
  }, [
    transformedAssets,
    locationLoading,
    locationError,
    hasUserLocation,
    linkedAsset,
    assetByMapData,
    failureDetails,
    failureModeData,
  ]);

  // Effect to automatically get location when identification method changes to "location"
  useEffect(() => {
    if (
      formValues.assetIdentificationMethod === "location" &&
      !hasUserLocation &&
      !locationLoading
    ) {
      getCurrentLocation();
    }
  }, [formValues.assetIdentificationMethod, hasUserLocation, locationLoading]);

  // Enhanced effect to populate form with failure details when in edit mode
  useEffect(() => {
    if (action === "edit" && failureDetails) {
      const populatedData = {
        ...defaultFormData,
        assetIdentificationMethod: mapFailureIdentificationMethod(
          failureDetails.failureIdentificationMethod
        ),

        // Asset selection based on identification method - FIXED
        ...(failureDetails.failureIdentificationMethod === 0 && {
          locationAssetSelection: failureDetails.asset?.id?.toString() || "",
        }),
        ...(failureDetails.failureIdentificationMethod === 1 && {
          mapAssetSelection: failureDetails.asset?.id.toString() || "",
        }),
        ...(failureDetails.failureIdentificationMethod === 2 && {
          selectedAssets: failureDetails.asset
            ? [failureDetails.asset.id.toString()]
            : [],
        }),

        // Fix priority mapping
        priority: failureDetails.priority,
        failureMode: failureDetails.failureModeCode || "",
        description: failureDetails.description || "",
        symptoms: parseObservedSymptoms(failureDetails.observedSymptoms),
        failureDateTime: failureDetails.failureAriseDate,

        additionalNotes: failureDetails.additionalNotes || "",
        photos: [],
        existingDocuments: failureDetails.documents || [],
      };

      // Set location data if available
      if (failureDetails.latitude && failureDetails.longitude) {
        setSelectedMapLocation({
          lat: failureDetails.latitude,
          lng: failureDetails.longitude,
        });
        setHasUserLocation(true);
      }

      // Set asset data for ALL identification methods when we have an asset ID
      if (failureDetails.asset) {
        const assetInfo = {
          id: failureDetails.asset?.id,
          assetNo:
            failureDetails.asset?.assetNo ||
            `Asset-${failureDetails.asset?.assetNo}`,
          assetName:
            failureDetails.asset?.assetName ||
            failureDetails.asset?.name ||
            `Asset ${failureDetails.asset}`,
          facilityName:
            failureDetails.asset?.facilityName ||
            failureDetails.asset?.facility ||
            "Unknown Facility",
          asset_no:
            failureDetails.asset?.assetNo || `Asset-${failureDetails.asset.id}`,
          name:
            failureDetails.asset?.assetName ||
            failureDetails.asset?.name ||
            `Asset ${failureDetails.asset}`,
          facility:
            failureDetails.asset?.facilityName ||
            failureDetails.asset?.facility ||
            "Unknown Facility",
        };

        setSelectedAsset(assetInfo);
        setLinkedAsset(assetInfo);
      }

      // Keep the original logic for manual method with asset details
      if (
        failureDetails.failureIdentificationMethod === 2 &&
        failureDetails.asset
      ) {
        setAssetDataForEdit(failureDetails.asset);
      }

      // Set existing documents
      if (failureDetails.documents && failureDetails.documents.length > 0) {
        setExistingDocuments(failureDetails.documents);
      }

      FormService.populateForm(form, populatedData, formFields);

      toast({
        title: "Loaded",
        description: "Failure details loaded for editing.",
      });
    }
  }, [action, failureDetails, form]);

  const handleMapLocationChange = (lat: number, lng: number) => {
    setSelectedMapLocation({ lat, lng });
    if (!hasUserLocation) {
      setHasUserLocation(true);
    }
  };

  const handleMapAddressChange = (address: string) => {
    // Address change handler
  };

  // Enhanced handleSubmit function with proper priority mapping
  const handleSubmit = async (data: any) => {
    try {
      const payload = new FormData();

      payload.append(
        "failure_identification_method",
        data.assetIdentificationMethod === "location"
          ? "0"
          : data.assetIdentificationMethod === "map"
          ? "1"
          : "2"
      );

      // Fix priority mapping using the helper function
      payload.append("priority", data.priority);

      payload.append("failure_mode_code", data.failureMode || "");
      payload.append("description", data.description || "");
      payload.append("observed_symptoms", JSON.stringify(data.symptoms || []));

      if (data.failureDateTime) {
        const formattedDate = formatDateForAPI(data.failureDateTime);
        payload.append("failure_arise_date", data.failureDateTime);
      } else {
        // Default to current date if no date provided
        payload.append("failure_arise_date", new Date().toISOString());
      }
      if (data.photos && data.photos.length > 0) {
        for (let i = 0; i < data.photos.length; i++) {
          payload.append("documents", data.photos[i]);
        }
      }

      const additionalData = {
        notes: data.additionalNotes || "",
      };

      payload.append("additional_notes", data.additionalNotes || "");
      payload.append("remote_utility_id", remoteUtilityId);

      // Enhanced asset ID extraction with better handling
      let assetId = "";
      if (
        data.assetIdentificationMethod === "location" &&
        data.locationAssetSelection
      ) {
        assetId = data.locationAssetSelection;
      } else if (
        data.assetIdentificationMethod === "map" &&
        data.mapAssetSelection
      ) {
        assetId = data.mapAssetSelection;
      } else if (
        data.assetIdentificationMethod === "manual" &&
        linkedAsset?.id
      ) {
        assetId = linkedAsset.id.toString();
      }

      payload.append("asset", assetId);

      // Use different mutations based on action
      if (action === "edit") {
        updateFailureMutation.mutateAsync({
          id: id,
          payload: payload,
        });
      } else {
        addFailureMutation.mutateAsync(payload);
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          action === "edit"
            ? "Failed to update failure report. Please try again."
            : "Failed to submit failure report. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    form.reset(defaultFormData);
    setFormFields(getFailureReportFields());
  };

  function onAssetSelect(asset: any): void {
    setSelectedAsset(asset);
    setLinkedAsset(asset);

    setTimeout(() => {
      FormService.clearErrors(form);
    }, 100);
  }

  // Helper functions for UI text based on mode
  const getPageTitle = () => {
    return action === "edit" ? "Edit Failure Report" : "Report New Failure";
  };

  const getPageSubtitle = () => {
    return action === "edit"
      ? "Update the failure report details"
      : "Report an asset failure for immediate attention";
  };

  const getSubmitButtonText = () => {
    return action === "edit" ? "Update Report" : "Submit Report";
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "locationAssetSelection" || "mapAssetSelection") {
      const options = getMapAssetOptions();
      const selectedOption = options.find((opt) => opt.value === value);

      if (selectedOption) {
        setSelectedAssetType(selectedOption.description);
      }
    }
  };
  return (
    <div className="space-y-6 pl-4">
      <div className="mb-4 flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/ax/field-alerts/failure/")}
        />
        <div className="flex flex-col">
          <PageHeader title={getPageTitle()} subtitle={getPageSubtitle()} />
        </div>
      </div>
      <div className="gap-6">
        <div className="lg:col-span-3">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            onReset={handleReset}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2 }}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              spacing: "comfortable",
              classes: FIELD_CLASSES,
            }}
            renderCustomFooter={() => (
              <div className="flex gap-3 pt-6 border-t border-gray-200 mt-8 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/ax/field-alerts/failure/")}
                >
                  Cancel
                </Button>
                {/* Only show Save as Draft for create mode */}
                {action !== "edit" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const draftData = form.getValues();
                        const payload = new FormData();

                        payload.append(
                          "failure_identification_method",
                          draftData.assetIdentificationMethod === "location"
                            ? "0"
                            : draftData.assetIdentificationMethod === "map"
                            ? "1"
                            : "2"
                        );

                        payload.append(
                          "priority",
                          mapPriorityToAPI(draftData.priority, priorityData)
                        );
                        payload.append(
                          "failure_mode_code",
                          draftData.failureMode || ""
                        );
                        payload.append(
                          "description",
                          draftData.description || ""
                        );
                        payload.append(
                          "observed_symptoms",
                          JSON.stringify(draftData.symptoms || [])
                        );

                        if (draftData.failureDateTime) {
                          const formattedDate = formatDateForAPI(
                            draftData.failureDateTime
                          );
                          payload.append("failure_arise_date", formattedDate);
                        } else {
                          // Default to current date if no date provided
                          payload.append(
                            "failure_arise_date",
                            new Date().toISOString()
                          );
                        }

                        if (draftData.photos && draftData.photos.length > 0) {
                          for (let i = 0; i < draftData.photos.length; i++) {
                            payload.append("documents", draftData.photos[i]);
                          }
                        }

                        payload.append(
                          "additional_notes",
                          draftData.additionalNotes || ""
                        );
                        payload.append("remote_utility_id", remoteUtilityId);

                        let assetId = "";
                        if (
                          draftData.assetIdentificationMethod === "location" &&
                          draftData.locationAssetSelection
                        ) {
                          assetId = draftData.locationAssetSelection;
                        } else if (
                          draftData.assetIdentificationMethod === "map" &&
                          draftData.mapAssetSelection
                        ) {
                          assetId = draftData.mapAssetSelection;
                        } else if (
                          draftData.assetIdentificationMethod === "manual" &&
                          linkedAsset?.id
                        ) {
                          assetId = linkedAsset.id.toString();
                        }

                        payload.append("asset", assetId);
                        payload.append("is_draft", "1");

                        await addFailureMutation.mutateAsync(payload);

                        toast({
                          title: "Draft Saved",
                          description:
                            "Your draft has been saved successfully.",
                        });

                        navigate("/ax/field-alerts/failure/");
                      } catch (error) {
                        toast({
                          title: "Error",
                          description:
                            "Failed to save draft. Please try again.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    Save as Draft
                  </Button>
                )}
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  isLoading={
                    updateFailureMutation.isPending ||
                    addFailureMutation.isPending
                  }
                >
                  {getSubmitButtonText()}
                </Button>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default Create;
